/**
 * Task Kanban View
 * 
 * Uses the global Kanban component with tasks-specific configuration.
 * All column definitions and mappings come from taskPhaseConfig.ts
 */

import { useState, useEffect } from 'react';
import { Task, TaskPhase } from 'shared/types/task';
import { TaskCard } from './taskCard';
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { getPhaseForColumn, type FrontendColumnId } from '../taskPhaseConfig';
import { Kanban } from 'shared/components/Kanban';
import { tasksKanbanConfig } from 'shared/components/Kanban/configs/tasksKanbanConfig';

interface TaskKanbanViewProps {
  tasks: Task[];
  onUpdate: () => void;
  onTaskClick: (task: Task) => void;
  onAddClick?: (phase: TaskPhase) => void;
}

export const TaskKanbanView = ({ 
  tasks, 
  onUpdate,
  onTaskClick,
  onAddClick
}: TaskKanbanViewProps) => {
  const { session } = useSession();
  
  // Local state for optimistic updates
  const [localTasks, setLocalTasks] = useState(tasks);
  
  // Update local state when prop changes
  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  /**
   * Handle drop event from the Kanban component
   * Performs optimistic update and syncs with backend
   */
  const handleDrop = async (
    itemId: string | number,
    newColumnId: FrontendColumnId,
    oldColumnId: FrontendColumnId
  ) => {
    const taskId = itemId as number;
    const newPhase = getPhaseForColumn(newColumnId);
    const oldPhase = getPhaseForColumn(oldColumnId);

    // Find the task being dragged
    const task = localTasks.find(t => t.id === taskId);
    if (!task) {
      return;
    }

    // If phase hasn't changed, no need to update
    if (task.phase === newPhase) {
      return;
    }

    // OPTIMISTIC UPDATE: Update local state immediately
    setLocalTasks((prev) => 
      prev.map(t => 
        t.id === taskId 
          ? { ...t, phase: newPhase as TaskPhase }
          : t
      )
    );

    // Skip backend call for dummy tasks (IDs >= 10000)
    if (taskId >= 10000) {
      toast.success(`Taak verplaatst naar "${newPhase}" (Dummy data)`);
      return;
    }

    // Make API call in background
    try {
      const response = await fetchWithAuth(
        `${API_URL}/tasks/update-phase`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ taskIds: [taskId], phase: newPhase }),
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to update task phase");
      }

      toast.success(`Taak verplaatst naar "${newPhase}"`);
    } catch (error: any) {
      console.error("Error updating task phase:", error);
      toast.error(`Fout bij verplaatsen taak: ${error.message || 'Onbekende fout'}`);
      
      // ROLLBACK: Revert local state on error
      setLocalTasks((prev) => 
        prev.map(t => 
          t.id === taskId 
            ? { ...t, phase: oldPhase }
            : t
        )
      );
      
      // Refetch to ensure data consistency
      onUpdate();
    }
  };

  /**
   * Handle add click from the Kanban component
   * Converts column ID to phase and calls parent handler
   */
  const handleAddClick = (columnId: FrontendColumnId) => {
    if (onAddClick) {
      const phase = getPhaseForColumn(columnId);
      onAddClick(phase);
    }
  };

  /**
   * Render a task card
   */
  const renderTaskCard = (task: Task) => (
    <TaskCard
      task={task}
      onClick={() => onTaskClick(task)}
      onUpdate={onUpdate}
    />
  );

  return (
    <Kanban
      config={tasksKanbanConfig}
      items={localTasks}
      renderCard={renderTaskCard}
      onDrop={handleDrop}
      onAddClick={handleAddClick}
      onCardClick={onTaskClick}
    />
  );
};

